import { ArrowBack, ArrowForward, MoreHoriz, Sync } from '@mui/icons-material'
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, Grid, Menu, MenuItem, MenuList, TextField, Tooltip, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import axios from 'axios'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import CashFlow, { BillingsWithCashFlow } from '../CashFlow/CashFlow'
import { AutoCompleteOptions, useLookupTables } from '../../../../hooks/useLookupTables'
import PreLoader from '../../../PreLoader'
import { NewEditButtons } from '../../../assets/NewEditButtons'
import { NewViewBilling } from '../types'
import { useAxios } from '../../../../hooks/useAxios'
import BillingsTable from './BillingsTable'
import { formatNumberToMoney } from '../../../Utils'
import BillingsOptions from './BillingsOptions'
import { toastError, toastSuccess } from '../../../assets/customToasts'
import { useMsal } from '@azure/msal-react'

export type Modal = {
  show: boolean
  billings?: BillingsWithCashFlow[]
}

type AreYouSureModal = {
  show: boolean
  type?: 'delete' | 'markAsPaid'
}

export type Filters = {
  affiliation: AutoCompleteOptions | null
  currency: AutoCompleteOptions | null
  defaultBillings: AutoCompleteOptions[]
  endDate: DateTime | null
  grades: AutoCompleteOptions[]
  referenceYear: number | null
  startDate: DateTime | null
  statuses: AutoCompleteOptions[]
  student: AutoCompleteOptions | null
  value: 'gross' | 'due' | 'paid'
}

type TableFormatCashFlow = {
  ids: number[]
  month: string,
  dueValue: number
  isPaid: boolean
  numberOfInstallments: number
  comments: string[]
}

type TableFormaBillings = {
  id: number
  defaultBillingName: string | null
  isBundle: boolean
  currencySign: string
  totalDueValue: number
  numberOfInstallments: number
  cashFlow: TableFormatCashFlow[]
}

export type TableFormat = {
  id: number
  customerName: string
  affiliationTag: string | null
  gradeName: string
  bundleDefaultBilling: string
  studentStatusTypeName: string
  billings: TableFormaBillings[]
}

function mergeBillings(customers: TableFormat[], index: number): TableFormat[] {
  // Base case
  if (index >= customers.length) return []

  const current = customers[index]
  const nextMerged = mergeBillings(customers, index + 1)

  if (nextMerged.length > 0 && nextMerged[0].customerName === current.customerName) {
    nextMerged[0].billings = current.billings.concat(nextMerged[0].billings)
    return nextMerged
  }
  else {
    // Returning the current person in the first index
    return [current, ...nextMerged]
  }
}

export default function Billings() {
  // Hooks
  const { lookupTables, errorLookupTables, isLoadingLookupTables } = useLookupTables(['affiliations', 'referenceYearForBillings'])
  const { accounts } = useMsal()
  // General
  const [enableMultipleSelect, setEnableMultipleSelect] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const [searchMethod, setSearchMethod] = useState<'referenceYear' | 'startEndDate'>('referenceYear')
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [selectedInstallments, setSelectedInstallments] = useState<number[]>([])
  const [modal, setModal] = useState<Modal>({
    show: false
  })
  const [areYouSureModal, setAreYouSureModal] = useState<AreYouSureModal>({
    show: false
  })
  // Filters
  const [filters, setFilters] = useState<Filters>({
    affiliation: null,
    currency: null,
    defaultBillings: [],
    endDate: DateTime.utc().endOf('year'),
    grades: [],
    referenceYear: DateTime.utc().get('year'),
    startDate: DateTime.utc().startOf('year'),
    statuses: [],
    student: null,
    value: 'due'
  })

  // useMemos
  const url = useMemo(() => {
    const qs: string[] = []

    if (!filters.affiliation || !filters.referenceYear) return ''

    if (filters.affiliation) qs.push(`fk_id_affiliation=${filters.affiliation.id}`)
    if (filters.referenceYear && searchMethod === 'referenceYear') qs.push(`reference_year=${filters.referenceYear}`)
    if (filters.startDate && filters.endDate && searchMethod === 'startEndDate') qs.push(`start_date=${filters.startDate}`, `end_date=${filters.endDate}`)
    qs.push(`include_payer=true`)

    return `${process.env.REACT_APP_MAIN_BACKEND_URL}/billings?${qs.join('&')}`
  }, [filters, searchMethod])

  // Data fetching
  const { data: customers, error: errorCustomers } = useAxios<AutoCompleteOptions[]>(`${process.env.REACT_APP_MAIN_BACKEND_URL}/customers?is_lookup=true&fk_id_customer_type=2&${filters.affiliation ? `fk_id_affiliation=${filters.affiliation.id}` : ''}`)

  const { data, error, isLoading, fetchData } = useAxios<NewViewBilling[]>(url)

  // Table operations
  const handleOpenBillings = useCallback((ids: number[]): void => {
    if (!data || !lookupTables) return

    const billings: BillingsWithCashFlow[] = data
      .filter((_) => ids.includes(_.id))
      .map((_) => ({
        billing: {
          id: _.id,
          affiliation: { id: _.fk_id_affiliation, label: lookupTables.affiliations.find((affiliation) => affiliation.id === _.fk_id_affiliation)?.label || '' },
          defaultBilling: _.fk_id_default_billing && _.default_billing_name ? { id: _.fk_id_default_billing, label: _.default_billing_name } : null,
          paymentMethod: null,
          issueDate: DateTime.fromISO(_.issue_date).setZone('America/New_York'),
          service: { id: _.fk_id_service, label: _.service_name },
          beneficiaryType: _.beneficiary.fk_id_customer_type,
          beneficiary: { id: _.fk_id_customer_beneficiary, label: _.customer_name_beneficiary },
          payerType: _.payer.fk_id_customer_type,
          payer: { id: _.fk_id_customer_payer, label: _.customer_name_payer },
          payee: _.fk_id_customer_payee,
          referenceYear: _.reference_year,
          currency: { id: _.fk_id_currency, sign: _.currency_sign },
          bundle_application_unique_id: _.bundle_application_unique_id,
          totalBilling: {
            value: _.total_due_value,
            label: formatNumberToMoney(_.total_due_value)
          }
        },
        discount: {
          type: 'percentage',
          percentage: {
            value: Math.round(((_.cash_flow.reduce((prev, curr) => prev + curr.gross_value, 0) - _.total_due_value) / _.cash_flow.reduce((prev, curr) => prev + curr.gross_value, 0)) * 100),
            label: Math.round(((_.cash_flow.reduce((prev, curr) => prev + curr.gross_value, 0) - _.total_due_value) / _.cash_flow.reduce((prev, curr) => prev + curr.gross_value, 0)) * 100).toString()
          },
          setValue: {
            value: 0,
            label: ''
          }
        },
        cashFlowOptions: {
          mode: 'numberOfBills',
          numberOfBills: {
            value: _.cash_flow.length,
            label: _.cash_flow.length.toString()
          },
          setValue: {
            value: 0,
            label: ''
          },
          dateOfFirstCashflow: DateTime.fromISO(_.cash_flow[0].due_date),
          remainder: 'first-bill'
        },
        cashFlowInstallments: _.cash_flow.map((installment) => ({
          id: installment.id,
          fk_id_billing: installment.fk_id_billing,
          installment_number: installment.installment_number,
          due_date: DateTime.fromISO(installment.due_date, { zone: 'America/New_York' }),
          paid_date: installment.paid_date ? DateTime.fromISO(installment.paid_date) : null,
          grossValue: {
            value: installment.gross_value,
            label: formatNumberToMoney(installment.gross_value)
          },
          dueValue: {
            value: installment.due_value,
            label: formatNumberToMoney(installment.due_value)
          },
          paid_value: installment.paid_value,
          fk_id_payment_method: installment.fk_id_payment_method,
          comment: installment.comment,
        }))
      }))

    setModal({
      show: true,
      billings
    })
  }, [data, lookupTables])

  const handleDelete = useCallback(async () => {
    const _toast = toast('Deleting billings', { toastId: 'toast-handle-delete', isLoading: true })

    // Variables to keep track success/errors
    var successCount = 0
    var errorCount = 0

    for (const id of selectedRows) {
      try {
        await axios({
          method: 'DELETE',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/billings/${id}`,
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          }
        })

        successCount++
      } catch (err) {
        console.log(err)

        errorCount++
      }

      toast.update(_toast, { render: `Deleting billings. Success: ${successCount}. Errors: ${errorCount}` })
    }

    if (errorCount > 0) {
      toastError(_toast, 'Could not delete billings.', 4000)
    }
    else {
      toastSuccess(_toast, `Billings deleted.`)
    }

    setAreYouSureModal({ show: false })

    // Update table
    fetchData()
    // Reset the selected rows state
    setSelectedRows([])
  }, [selectedRows, fetchData, setSelectedRows, setAreYouSureModal])

  const handleMarkAsPaid = useCallback(async () => {
    const _toast = toast('Marking installments as paid', { toastId: 'toast-handle-mark-as-paid', isLoading: true })

    // Variables to keep track success/errors
    var successCount = 0
    var errorCount = 0

    for (const id of selectedInstallments) {
      try {
        await axios({
          method: 'PUT',
          url: `${process.env.REACT_APP_MAIN_BACKEND_URL}/cash-flow/${id}?markAsPaid=true`,
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_MAIN_BACKEND_TOKEN}`
          },
          data: {
            paid_date: DateTime.now().toISO(),
            updated_by: accounts[0]?.username || 'Cypress testing'
          }
        })

        successCount++
      } catch (err) {
        console.log(err)

        errorCount++
      }

      toast.update(_toast, { render: `Marking installments as paid. Success: ${successCount}. Errors: ${errorCount}` })
    }

    if (errorCount > 0) {
      toastError(_toast, 'Could not mark installments as paid.', 4000)
    }
    else {
      toastSuccess(_toast, `Installments marked as paid.`)
    }

    setAreYouSureModal({ show: false })

    // Update table
    fetchData()
  }, [selectedInstallments, accounts, setAreYouSureModal, fetchData])

  // Filters
  const filterByCurrency = useCallback((row: NewViewBilling) => {
    switch (filters.currency) {
      case null:
        return true
      default:
        return row.fk_id_currency === filters.currency.id
    }
  }, [filters.currency])

  const filterByCustomer = useCallback((row: NewViewBilling) => {
    switch (filters.student) {
      case null:
        return true
      default:
        return row.fk_id_customer_beneficiary === filters.student.id
    }
  }, [filters.student])

  const filterByDefaultBilling = useCallback((row: NewViewBilling) => {
    if (!filters.defaultBillings.length) return true

    if (!row.fk_id_default_billing) return false

    else if (filters.defaultBillings.map((_) => _.id).includes(row.fk_id_default_billing)) return true

    return false
  }, [filters.defaultBillings])

  const filterByGrade = useCallback((row: NewViewBilling) => {
    if (!filters.grades.length) return true

    return filters.grades.map((_) => _.id).includes(row.beneficiary.fk_id_grade)
  }, [filters.grades])

  const filterByStudentStatusType = useCallback((row: NewViewBilling) => {
    if (!filters.statuses.length) return true

    return filters.statuses.map((_) => _.id).includes(row.beneficiary.fk_id_student_status_type)
  }, [filters.statuses])

  const filteredData = useMemo(() => data ?
    data
      .filter(filterByCurrency)
      .filter(filterByCustomer)
      .filter(filterByDefaultBilling)
      .filter(filterByGrade)
      .filter(filterByStudentStatusType)
    :
    [],
    [
      data,
      filterByCurrency,
      filterByCustomer,
      filterByDefaultBilling,
      filterByGrade,
      filterByStudentStatusType
    ])

  const months = useMemo(() => {
    const months: DateTime[] = []

    var startDate: DateTime | null = null
    var endDate: DateTime | null = null

    if (searchMethod === 'referenceYear') {
      // Find the start and end date
      const allDueDates = filteredData.flatMap((billing) => billing.cash_flow.map((installment) => installment.due_date)).sort((a, b) => DateTime.fromISO(a).diff(DateTime.fromISO(b)).milliseconds)

      startDate = DateTime.fromISO(allDueDates[0], { zone: 'UTC' })
      endDate = DateTime.fromISO(allDueDates[allDueDates.length - 1], { zone: 'UTC' })
    }
    // Start and End date
    else {
      if (!filters.startDate || !filters.endDate) return months

      startDate = filters.startDate
      endDate = filters.endDate
    }

    var currentMonth = startDate.startOf('month')

    // Max of 24 months to render
    while (currentMonth.diff(endDate).milliseconds <= 0 && months.length <= 24) {
      months.push(currentMonth)

      currentMonth = currentMonth.plus({ month: 1 })
    }

    return months
  }, [filteredData, searchMethod, filters.startDate, filters.endDate])

  const tableFormat: TableFormat[] = useMemo(() => {
    const dataInTableFormat: TableFormat[] = filteredData
      .map((billing) => {
        const totalDueValue = billing.total_due_value
        const numberOfInstallments = billing.cash_flow.length

        const cashFlow = months
          .map((month) => {
            const installments = billing.cash_flow
              .filter((installment) => month.startOf('month').diff(DateTime.fromISO(installment.due_date)).milliseconds <= 0 && month.endOf('month').diff(DateTime.fromISO(installment.due_date)).milliseconds >= 0)

            return {
              ids: installments.map((_) => _.id),
              month: month.toFormat('MMM yy'),
              dueValue: installments.length ? installments.reduce((prev, curr) => prev += curr.due_value, 0) : null,
              isPaid: installments.every((_) => _.paid_value),
              numberOfInstallments: installments.length,
              comments: installments.map((_) => _.comment).filter((_) => _ !== null)
            }
          })
          .filter((_) => _.dueValue !== null) as TableFormatCashFlow[]

        return {
          id: billing.beneficiary.dependence_id,
          customerName: billing.customer_name_beneficiary,
          affiliationTag: billing.beneficiary.fk_id_affiliation === filters.affiliation?.id ? null : billing.beneficiary.affiliation_tag,
          gradeName: billing.beneficiary.grade_name,
          bundleDefaultBilling: billing.bundle_name || billing.default_billing_name || billing.service_name,
          studentStatusTypeName: billing.beneficiary.student_status_type_name,
          billings: [{
            id: billing.id,
            defaultBillingName: billing.default_billing_name || billing.service_name,
            isBundle: Boolean(billing.fk_id_bundle),
            currencySign: billing.currency_sign,
            totalDueValue,
            numberOfInstallments,
            cashFlow
          }]
        }
      })

    return mergeBillings(dataInTableFormat, 0)
  }, [filteredData, months, filters.affiliation])

  if (isLoadingLookupTables) return <PreLoader />

  if (error || errorLookupTables || errorCustomers) return <Alert severity='error'>Could not load Billings page.</Alert>

  console.log('Billings: lookup ref year', lookupTables?.referenceYearForBillings)

  return <>
    {/* Cash Flow modal */}
    <Dialog
      id='cash-flow'
      open={modal.show}
      onClose={() => setModal({ show: false })}
      maxWidth={false}
    >
      <CashFlow
        onClose={() => setModal({ show: false })}
        presetBillings={modal.billings}
        refreshTable={async () => { }}
      />
    </Dialog>
    {/* Options modal */}
    <Dialog
      id='modal-options'
      open={areYouSureModal.show}
      onClose={() => setAreYouSureModal({ show: false })}
      maxWidth={false}
    >
      <DialogContent>
        <Typography>
          {areYouSureModal.type === 'delete' ? 'Are you sure you want to delete this billings?' : 'Are you sure you want to mark this installments as paid?'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          id='button-cancel'
          onClick={() => setAreYouSureModal({ show: false })}
        >
          Cancel
        </Button>
        <Button
          id='button-confirm'
          onClick={areYouSureModal.type === 'delete' ? () => handleDelete() : () => handleMarkAsPaid()}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
    {/* Filters */}
    <Grid item xs={.75}>
      <Tooltip title='Change search method'>
        <Button
          id='button-change-search-method'
          onClick={() => setSearchMethod((prev) => prev === 'referenceYear' ? 'startEndDate' : 'referenceYear')}
        >
          <Sync />
        </Button>
      </Tooltip>
    </Grid>
    {
      searchMethod === 'referenceYear' ?
        <>
          <Grid item xs={1.5}>
            <TextField
              id='filter-reference-year'
              label='Reference year'
              value={filters.referenceYear || ''}
              onChange={(e) => setFilters((_filters) => ({ ..._filters, referenceYear: Number(e.target.value) }))}
              select
            >
              {
                // Years with billings, plus the current and the next one (rollout)
                Array.from(new Set([...(lookupTables?.referenceYearForBillings || []), DateTime.utc().year, DateTime.utc().year + 1])).sort().map((year, index) => <MenuItem key={index} id={`reference-year-option-${year}`} value={year}>{year}</MenuItem>)
              }
            </TextField>
          </Grid>
        </>
        :
        <>
          <Grid item xs={.5}>
            <DateButtons
              id='start-date'
              onClickPrev={() => setFilters((_filters) => ({ ..._filters, startDate: _filters.startDate?.minus({ month: 1 }).startOf('month') || null }))}
              onClickNext={() => setFilters((_filters) => ({ ..._filters, startDate: _filters.startDate?.plus({ month: 1 }).startOf('month') || null }))}
            />
          </Grid >
          <Grid item xs={1.25}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                label='Start Date'
                format="dd-MMM-yy"
                value={filters.startDate}
                onChange={(newValue) => setFilters({ ...filters, startDate: newValue })}
                maxDate={filters.endDate}
                slotProps={{ textField: { id: 'filter-start-date' } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={.5}>
            <DateButtons
              id='end-date'
              onClickPrev={() => setFilters((_filters) => ({ ..._filters, endDate: _filters.endDate?.minus({ month: 1 }).endOf('month') || null }))}
              onClickNext={() => setFilters((_filters) => ({ ..._filters, endDate: _filters.endDate?.plus({ month: 1 }).endOf('month') || null }))}
            />
          </Grid>
          <Grid item xs={1.25}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                label='End Date'
                format="dd-MMM-yy"
                value={filters.endDate}
                onChange={(newValue) => setFilters({ ...filters, endDate: newValue })}
                minDate={filters.startDate}
                slotProps={{ textField: { id: 'filter-end-date' } }}
              />
            </LocalizationProvider>
          </Grid>
        </>
    }
    {/* Filters */}
    <Grid item xs={1.75}>
      <Autocomplete
        id='filter-affiliation'
        options={lookupTables?.affiliations || []}
        value={filters.affiliation}
        onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, affiliation: newValue }))}
        renderInput={(params) => <TextField {...params} label="Affiliation" />}
      />
    </Grid>
    <Grid item xs={2.5}>
      <Autocomplete
        id='filter-student'
        options={customers || []}
        value={filters.student}
        onChange={(e, newValue) => setFilters((_filters) => ({ ..._filters, student: newValue }))}
        renderInput={(params) => <TextField {...params} label="Student" />}
        disabled={!customers || customers.length === 0}
      />
    </Grid>
    {/* Spacer */}
    <Grid item xs={searchMethod === 'referenceYear' ? 2.25 : .25} />
    {/* Buttons */}
    <NewEditButtons
      _new={{ label: 'New Billing', onClick: () => setModal({ show: true }), width: 1.5, height: 48 }}
      _delete={{ disabled: !selectedRows.length, onClick: handleDelete, height: 48 }}
    />
    <Grid item xs={.75}>
      <Button
        id='button-more-options'
        onClick={(e) => setMenuAnchorEl(e.currentTarget)}
      >
        <MoreHoriz />
      </Button>
      {/* Menu */}
      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={() => setMenuAnchorEl(null)}
      >
        <MenuList>
          <BillingsOptions
            data={data || []}
            filters={filters}
            handleMarkAsPaid={handleMarkAsPaid}
            selectedInstallments={selectedInstallments}
            setEnableMultipleSelect={setEnableMultipleSelect}
            setFilters={setFilters}
            tableFormat={tableFormat}
          />
        </MenuList>
      </Menu>
    </Grid>
    {/* Table */}
    <Grid item xs={12} height={'75vh'}>
      {
        isLoading ?
          <PreLoader height={'100%'} />
          :
          <BillingsTable
            data={tableFormat}
            enableMultipleSelect={enableMultipleSelect}
            handleOpenBillings={handleOpenBillings}
            months={months}
            selectedInstallments={selectedInstallments}
            selectedRows={selectedRows}
            setSelectedInstallments={setSelectedInstallments}
            setSelectedRows={setSelectedRows}
          />
      }
    </Grid>
  </>
}

type DateButtonsProps = {
  id: string
  onClickPrev: () => void
  onClickNext: () => void
}

function DateButtons({ id, onClickPrev, onClickNext }: DateButtonsProps) {
  return <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
    <button
      id={`button-prev-${id}`}
      style={{ width: '100%', height: '47.5%', backgroundColor: 'white', borderRadius: 4, display: 'grid', placeItems: 'center' }}
      onClick={onClickPrev}
    >
      <ArrowBack sx={{ height: 14, width: 14 }} />
    </button>
    <button
      id={`button-next-${id}`}
      style={{ width: '100%', height: '47.5%', backgroundColor: 'white', borderRadius: 4, display: 'grid', placeItems: 'center' }}
      onClick={onClickNext}
    >
      <ArrowForward sx={{ height: 14, width: 14 }} />
    </button>
  </div>
}